import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { faUserShield } from '@fortawesome/free-solid-svg-icons';
import CrudManager from '../../../components/common/CrudManager';
import RoleService from '../../../services/RoleService';
import Alert from '../../../components/common/Alert';

const ACCESS_KEYS = ['canRead', 'canCreate', 'canUpdate', 'canDelete', 'canReport'];

const normalizeAccessRows = (rows) => (Array.isArray(rows) ? rows : []).map((row) => ({
  menuId: row.menuId ?? null,
  menuName: row.menuName ?? '',
  menuTranslationKey: row.menuTranslationKey ?? '',
  submenuId: row.submenuId ?? null,
  submenuName: row.submenuName ?? '',
  submenuTranslationKey: row.submenuTranslationKey ?? '',
  canRead: row.submenuId ? Boolean(row.canRead) : false,
  canCreate: row.submenuId ? Boolean(row.canCreate) : false,
  canUpdate: row.submenuId ? Boolean(row.canUpdate) : false,
  canDelete: row.submenuId ? Boolean(row.canDelete) : false,
  canReport: row.submenuId ? Boolean(row.canReport) : false,
  enabled: Boolean(row.enabled),
}));

const toAccessPayload = (rows) => (Array.isArray(rows) ? rows : []).map((row) => ({
  menuId: row.menuId ?? null,
  submenuId: row.submenuId ?? null,
  canRead: row.submenuId ? Boolean(row.canRead) : false,
  canCreate: row.submenuId ? Boolean(row.canCreate) : false,
  canUpdate: row.submenuId ? Boolean(row.canUpdate) : false,
  canDelete: row.submenuId ? Boolean(row.canDelete) : false,
  canReport: row.submenuId ? Boolean(row.canReport) : false,
  enabled: Boolean(row.enabled),
}));

const RoleFormContent = ({ formData, onChange, isEditing }) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('info');
  const [accessRows, setAccessRows] = useState([]);
  const [loadingAccesses, setLoadingAccesses] = useState(false);
  const [accessError, setAccessError] = useState('');

  const roleId = useMemo(() => {
    const parsed = Number(formData?.id);
    return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
  }, [formData?.id]);

  useEffect(() => {
    setActiveTab('info');
  }, [roleId, isEditing]);

  useEffect(() => {
    const loadAccesses = async () => {
      setLoadingAccesses(true);
      setAccessError('');
      try {
        const rows = isEditing && roleId
          ? await RoleService.getAccesses(roleId)
          : await RoleService.getAccessCatalog();
        setAccessRows(normalizeAccessRows(rows));
      } catch (error) {
        setAccessRows([]);
        setAccessError(error?.message || t('common.errorOccurred'));
      } finally {
        setLoadingAccesses(false);
      }
    };

    loadAccesses();
  }, [isEditing, roleId, t]);

  useEffect(() => {
    const nextPayload = toAccessPayload(accessRows);
    const currentPayload = toAccessPayload(formData?.accesses || []);

    if (JSON.stringify(nextPayload) !== JSON.stringify(currentPayload)) {
      onChange({
        ...formData,
        accesses: nextPayload,
      });
    }
  }, [accessRows, formData, onChange]);

  const handleBasicFieldChange = (field, value) => {
    onChange({
      ...formData,
      [field]: value,
    });
  };

  const handleMenuEnabledToggle = (menuId, checked) => {
    setAccessRows((prev) => prev.map((row) => {
      if (row.menuId !== menuId) {
        return row;
      }

      if (!row.submenuId) {
        return {
          ...row,
          enabled: checked,
          canRead: false,
          canCreate: false,
          canUpdate: false,
          canDelete: false,
          canReport: false,
        };
      }

      const updated = { ...row, enabled: checked };
      ACCESS_KEYS.forEach((key) => {
        updated[key] = checked;
      });
      return updated;
    }));
  };

  const handleSubmenuEnabledToggle = (index, checked) => {
    setAccessRows((prev) => {
      const target = prev[index];
      if (!target) return prev;
      const parentMenuId = target.menuId;

      const updatedRows = prev.map((row, rowIndex) => {
        if (rowIndex !== index) return row;
        const updated = { ...row, enabled: checked };
        ACCESS_KEYS.forEach((key) => {
          updated[key] = checked;
        });
        return updated;
      });

      const hasEnabledSubmenu = updatedRows.some((row) => row.menuId === parentMenuId && row.submenuId && row.enabled);

      return updatedRows.map((row) => {
        if (row.menuId === parentMenuId && !row.submenuId) {
          return {
            ...row,
            enabled: hasEnabledSubmenu,
            canRead: false,
            canCreate: false,
            canUpdate: false,
            canDelete: false,
            canReport: false,
          };
        }
        return row;
      });
    });
  };

  const handleSubmenuActionToggle = (index, permissionKey, checked) => {
    setAccessRows((prev) => {
      const target = prev[index];
      if (!target) return prev;
      const parentMenuId = target.menuId;

      const updatedRows = prev.map((row, rowIndex) => {
        if (rowIndex !== index) return row;
        const updated = { ...row, [permissionKey]: checked };
        const hasAnyPermission = ACCESS_KEYS.some((key) => Boolean(updated[key]));
        updated.enabled = hasAnyPermission;
        return updated;
      });

      const hasEnabledSubmenu = updatedRows.some((row) => row.menuId === parentMenuId && row.submenuId && row.enabled);

      return updatedRows.map((row) => {
        if (row.menuId === parentMenuId && !row.submenuId) {
          return {
            ...row,
            enabled: hasEnabledSubmenu,
            canRead: false,
            canCreate: false,
            canUpdate: false,
            canDelete: false,
            canReport: false,
          };
        }
        return row;
      });
    });
  };

  const handleToggleAll = (checked) => {
    setAccessRows((prev) => prev.map((row) => {
      if (!row.submenuId) {
        return {
          ...row,
          enabled: checked,
          canRead: false,
          canCreate: false,
          canUpdate: false,
          canDelete: false,
          canReport: false,
        };
      }
      const updated = { ...row, enabled: checked };
      ACCESS_KEYS.forEach((key) => {
        updated[key] = checked;
      });
      return updated;
    }));
  };

  return (
    <div className="tabs">
      <ul className="nav nav-tabs">
        <li className={activeTab === 'info' ? 'active' : ''}>
          <a
            href="#role-info"
            onClick={(event) => {
              event.preventDefault();
              setActiveTab('info');
            }}
            style={{ cursor: 'pointer' }}
          >
            {t('role.tabs.info')}
          </a>
        </li>
        <li className={activeTab === 'access' ? 'active' : ''}>
          <a
            href="#role-access"
            onClick={(event) => {
              event.preventDefault();
              setActiveTab('access');
            }}
            style={{ cursor: 'pointer' }}
          >
            {t('role.tabs.access')}
          </a>
        </li>
      </ul>

      <div className="tab-content" style={{ paddingTop: '15px' }}>
        {activeTab === 'info' && (
          <div className="tab-pane active">
            <div className="form-group">
              <label htmlFor="roleName">
                <i className="fa fa-id-badge"></i> {t('role.roleName')} <span className="required-field">*</span>
              </label>
              <input
                id="roleName"
                type="text"
                className="form-control"
                value={formData?.roleName || ''}
                onChange={(event) => handleBasicFieldChange('roleName', event.target.value)}
                placeholder={t('role.roleNamePlaceholder')}
              />
            </div>

            <div className="form-group">
              <label htmlFor="roleDescription">
                <i className="fa fa-align-left"></i> {t('role.description')}
              </label>
              <input
                id="roleDescription"
                type="text"
                className="form-control"
                value={formData?.description || ''}
                onChange={(event) => handleBasicFieldChange('description', event.target.value)}
                placeholder={t('role.descriptionPlaceholder')}
              />
            </div>
          </div>
        )}

        {activeTab === 'access' && (
          <div className="tab-pane active">
            <Alert
              type="error"
              message={accessError}
              onClose={() => setAccessError('')}
              autoClose={3000}
            />

            {loadingAccesses ? (
              <div style={{ padding: '10px 0' }}>{t('common.loading')}</div>
            ) : (
              <div className="table-responsive">
                <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
                  <button
                    type="button"
                    className="btn btn-default btn-sm"
                    onClick={() => handleToggleAll(true)}
                  >
                    {t('role.access.markAll', { defaultValue: 'Activar todo' })}
                  </button>
                  <button
                    type="button"
                    className="btn btn-default btn-sm"
                    onClick={() => handleToggleAll(false)}
                  >
                    {t('role.access.unmarkAll', { defaultValue: 'Desactivar todo' })}
                  </button>
                </div>

                <table className="table table-bordered table-hover">
                  <thead>
                    <tr>
                      <th>{t('role.access.menu')}</th>
                      <th>{t('role.enabled')}</th>
                      <th>{t('role.access.read')}</th>
                      <th>{t('role.access.create')}</th>
                      <th>{t('role.access.update')}</th>
                      <th>{t('role.access.delete')}</th>
                      <th>{t('role.access.report')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {accessRows.map((row, index) => {
                      const isSubmenu = Boolean(row.submenuId);
                      const label = isSubmenu
                        ? (row.submenuTranslationKey ? t(row.submenuTranslationKey) : row.submenuName)
                        : (row.menuTranslationKey ? t(row.menuTranslationKey) : row.menuName);

                      return (
                        <tr key={`${row.menuId || 'm'}-${row.submenuId || 'root'}`} style={!isSubmenu ? { backgroundColor: '#f8f8f8', fontWeight: 600 } : {}}>
                          <td>{isSubmenu ? `- ${label}` : label}</td>
                          <td>
                            <input
                              type="checkbox"
                              checked={Boolean(row.enabled)}
                              onChange={(e) => {
                                if (isSubmenu) {
                                  handleSubmenuEnabledToggle(index, e.target.checked);
                                } else {
                                  handleMenuEnabledToggle(row.menuId, e.target.checked);
                                }
                              }}
                              title={!isSubmenu ? t('role.access.menuAndSubmenus', { defaultValue: 'Activar menú y todos sus submenús' }) : ''}
                            />
                          </td>
                          <td>
                            {isSubmenu ? (
                              <input type="checkbox" checked={Boolean(row.canRead)} onChange={(e) => handleSubmenuActionToggle(index, 'canRead', e.target.checked)} />
                            ) : (
                              <span>-</span>
                            )}
                          </td>
                          <td>
                            {isSubmenu ? (
                              <input type="checkbox" checked={Boolean(row.canCreate)} onChange={(e) => handleSubmenuActionToggle(index, 'canCreate', e.target.checked)} />
                            ) : (
                              <span>-</span>
                            )}
                          </td>
                          <td>
                            {isSubmenu ? (
                              <input type="checkbox" checked={Boolean(row.canUpdate)} onChange={(e) => handleSubmenuActionToggle(index, 'canUpdate', e.target.checked)} />
                            ) : (
                              <span>-</span>
                            )}
                          </td>
                          <td>
                            {isSubmenu ? (
                              <input type="checkbox" checked={Boolean(row.canDelete)} onChange={(e) => handleSubmenuActionToggle(index, 'canDelete', e.target.checked)} />
                            ) : (
                              <span>-</span>
                            )}
                          </td>
                          <td>
                            {isSubmenu ? (
                              <input type="checkbox" checked={Boolean(row.canReport)} onChange={(e) => handleSubmenuActionToggle(index, 'canReport', e.target.checked)} />
                            ) : (
                              <span>-</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const Role = () => {
  const columns = [
    {
      key: 'roleName',
      label: 'role.roleName',
      sortable: true,
      showInForm: true,
      type: 'text',
      placeholder: 'role.roleNamePlaceholder',
      required: true,
      icon: 'fa-id-badge',
      showInFilter: true,
      filterPlaceholder: 'role.roleName',
      filterSize: 'large'
    },
    {
      key: 'description',
      label: 'role.description',
      sortable: true,
      showInForm: true,
      type: 'text',
      placeholder: 'role.descriptionPlaceholder',
      icon: 'fa-align-left',
      showInFilter: true,
      filterPlaceholder: 'role.description',
      filterSize: 'large'
    },
    {
      key: 'enabled',
      label: 'role.enabled',
      showInGrid: false,
      sortable: false,
      showInFilter: true,
      filterType: 'checkbox',
      filterSize: 'small'
    }
  ];

  return (
    <CrudManager
      service={RoleService}
      columns={columns}
      entityName="role"
      icon={faUserShield}
      formLayout="inline"
      initialFormData={{
        roleName: '',
        description: '',
        enabled: true,
        accesses: [],
      }}
      renderForm={({ formData, onChange, isEditing }) => (
        <RoleFormContent formData={formData} onChange={onChange} isEditing={isEditing} />
      )}
    />
  );
};

export default Role;

